import { Component, type ReactNode } from "react";
import { AlertTriangle, RotateCcw } from "lucide-react";
import { Button } from "@/components/ui/button";

interface Props {
  children: ReactNode;
}

interface State {
  hasError: boolean;
  error?: Error;
}

class ErrorBoundary extends Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = { hasError: false };
  }

  static getDerivedStateFromError(error: Error): State {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, info: React.ErrorInfo) {
    console.error("[ErrorBoundary] caught:", error);
    console.error("[ErrorBoundary] message:", error?.message);
    console.error("[ErrorBoundary] stack:", error?.stack);
    console.error("[ErrorBoundary] componentStack:", info.componentStack);
  }

  handleReset = () => {
    this.setState({ hasError: false, error: undefined });
  };

  render() {
    if (this.state.hasError) {
      const msg = this.state.error?.message || "Sin mensaje";
      const stack = this.state.error?.stack || "";
      return (
        <div className="flex min-h-[60vh] flex-col items-center justify-center gap-4 p-8 text-center">
          <AlertTriangle className="h-12 w-12 text-destructive" />
          <h2 className="text-lg font-semibold text-foreground">
            Algo salió mal
          </h2>
          <p className="max-w-md text-sm text-muted-foreground">
            Se produjo un error inesperado. Puedes intentar recargar la página.
          </p>
          <details className="max-w-2xl text-left text-xs text-muted-foreground bg-muted/30 p-3 rounded border">
            <summary className="cursor-pointer font-medium">Detalles técnicos</summary>
            <p className="mt-2 font-mono text-destructive">{msg}</p>
            <pre className="mt-2 whitespace-pre-wrap break-words max-h-64 overflow-auto">{stack}</pre>
          </details>
          <div className="flex gap-3">
            <Button onClick={this.handleReset} variant="outline" className="gap-2">
              <RotateCcw className="h-4 w-4" />
              Reintentar
            </Button>
            <Button onClick={() => window.location.reload()} className="gap-2">
              Recargar página
            </Button>
          </div>
        </div>
      );
    }

    return <>{this.props.children}</>;
  }
}

export default ErrorBoundary;
